import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Bell, Plus } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';

export default function Notices() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', category: 'general', audience: 'tenant', property_id: '' });
  const load = () => api.get('/notices').then(r => setItems(r.data));
  useEffect(() => { load(); api.get('/properties').then(r => setProps(r.data)); }, []);
  const submit = async () => {
    try { await api.post('/notices', { ...form, property_id: form.property_id || null }); toast.success('Notice issued'); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div>
      <PageHeader title="Notices" subtitle="Communicate with tenants and owners" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="compose-notice-button"><Plus className="h-4 w-4 mr-1" /> New notice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Compose notice</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notice-title-input" /></div>
              <div className="col-span-2"><Label>Body</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="rent">Rent</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="lease">Lease</SelectItem></SelectContent></Select></div>
              <div><Label>Audience</Label><Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tenant">Tenants</SelectItem><SelectItem value="owner">Owners</SelectItem><SelectItem value="all">Everyone</SelectItem></SelectContent></Select></div>
              <div className="col-span-2"><Label>Property (optional)</Label><Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}><SelectTrigger><SelectValue placeholder="All" /></SelectTrigger><SelectContent>{props.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="notice-submit-button">Issue notice</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <div className="space-y-3">
        {items.map((n) => (
          <Card key={n.id} className="p-4 card-hover" data-testid={`notice-item-${n.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-[hsl(var(--role-accent)/0.12)] text-[hsl(var(--role-accent))]"><Bell className="h-4 w-4" /></div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(n.created_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground capitalize">{n.category} · {n.audience}</div>
                <div className="text-sm mt-2 whitespace-pre-line">{n.body}</div>
                <div className="text-xs text-muted-foreground mt-2">Acknowledged by {n.acknowledged_by?.length || 0}</div>
              </div>
            </div>
          </Card>
        ))}
        {!items.length && <Card className="p-10 text-center text-muted-foreground">No notices yet.</Card>}
      </div>
    </div>
  );
}
